<template>
  <div class="max-w-3xl" v-if="subscription">

    <!-- Resumen de la suscripción actual -->
    <section class="bg-white shadow sm:rounded-md p-6 mb-6">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg leading-6 font-medium text-gray-900">{{ $t('settings.your_subscription') }}</h2>
        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="statusBadge.classes">
          {{ statusBadge.label }}
        </span>
      </div>

      <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
        <div>
          <dt class="text-sm font-medium text-gray-500">{{ $t('general.plan') }}</dt>
          <dd class="mt-1 text-sm text-gray-900 font-semibold">{{ subscription.title }}</dd>
        </div>
        <div>
          <dt class="text-sm font-medium text-gray-500">{{ $t('general.total') }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ subscription.total }} €</dd>
        </div>
        <div v-if="billingInfo">
          <dt class="text-sm font-medium text-gray-500">{{ billingInfo.label }}</dt>
          <dd class="mt-1 text-sm text-gray-900">{{ billingInfo.value }}</dd>
        </div>
      </dl>
    </section>

    <!-- Acción principal: cambiar método de pago, o activar plan si está en prueba -->
    <section class="bg-white shadow sm:rounded-md p-6 mb-3">
      <div v-if="subscription.status === 'trialing'" class="flex justify-center">
        <router-link to="/myaccount/current-subscription" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-bpblue hover:bg-bpbluedeep focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bpblue">
          {{ $t('settings.activate_plan_now') }}
        </router-link>
      </div>
      <stripe-number-component v-else></stripe-number-component>
    </section>

    <!-- Contacto para cancelar suscripción: discreto, sin protagonismo -->
    <p class="text-right text-xs text-gray-400 mb-6 pr-1">
      {{ $t('settings.cancel_subscription_text') }}
      <a href="mailto:billing@brainspro.com" class="text-bpblue hover:text-bpbluedeep underline">billing@brainspro.com</a>
    </p>

    <!-- Historial de facturas (pendiente de implementar) -->
    <section class="bg-white shadow sm:rounded-md p-6 text-center">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mx-auto h-10 w-10 text-gray-300 mb-2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
      <p class="text-sm text-gray-500">{{ $t('settings.invoices_coming_soon') }}</p>
    </section>

  </div>
</template>

<script>
import StripeNumberComponent from "@/views/MyAccount/Subscription/StripeNumberComponent";
import {useAuthUserStore} from "@/stores/auth";
import {useAcademiesStore} from "@/stores/academies";

export default {
  name: "BillingComponent",
  components: {
    StripeNumberComponent,
  },
  setup() {
    const authUserStore = useAuthUserStore();
    const academiesStore = useAcademiesStore();

    return {
      authUserStore,
      academiesStore,
    };
  },
  mounted() {
    this.academiesStore.getAcademy()
  },
  computed: {
    subscription() {
      return this.academiesStore.academy.saas_subscriptions?.[0]
    },
    statusBadge() {
      const badges = {
        trialing: { label: this.$t('settings.status_trialing'), classes: 'bg-amber-100 text-amber-800' },
        active: { label: this.$t('settings.status_active'), classes: 'bg-green-100 text-green-800' },
        manual: { label: this.$t('settings.status_active'), classes: 'bg-green-100 text-green-800' },
        past_due: { label: this.$t('settings.status_past_due'), classes: 'bg-gray-100 text-gray-600' },
        unpaid: { label: this.$t('settings.status_unpaid'), classes: 'bg-red-100 text-red-800' },
        canceled: { label: this.$t('settings.status_canceled'), classes: 'bg-gray-100 text-gray-600' },
        incomplete: { label: this.$t('settings.status_incomplete'), classes: 'bg-gray-100 text-gray-600' },
        incomplete_expired: { label: this.$t('settings.status_incomplete'), classes: 'bg-gray-100 text-gray-600' },
      }

      return badges[this.subscription.status] ?? { label: this.subscription.status, classes: 'bg-gray-100 text-gray-600' }
    },
    hasValidFutureEndDate() {
      if (!this.subscription.ends_at) return false

      let endsAt = this.$moment(this.subscription.ends_at)
      return endsAt.isValid() && endsAt.isAfter(this.$moment())
    },
    billingInfo() {
      const status = this.subscription.status

      if (status === 'trialing') {
        return {
          label: this.$t('settings.trial_ends_on'),
          value: this.$moment(this.subscription.trial_ends_at).format('DD-MM-YYYY')
        }
      }

      if (status === 'manual') {
        if (this.isAnnualTitle(this.subscription.title)) {
          if (this.hasValidFutureEndDate) {
            return {
              label: this.$t('settings.next_renewal'),
              value: this.$moment(this.subscription.ends_at).format('DD-MM-YYYY')
            }
          }
          return { label: this.$t('settings.billing_type'), value: this.$t('settings.billing_type_yearly') }
        }
        return { label: this.$t('settings.billing_type'), value: this.$t('settings.manual_recurring') }
      }

      if (status === 'active') {
        return { label: this.$t('settings.billing_type'), value: this.$t('settings.auto_renews') }
      }

      return null
    }
  },
  methods: {
    isAnnualTitle(title) {
      return /\banual\b/i.test(title ?? '')
    }
  }
}
</script>

<style scoped>

</style>
